import React, { FC } from 'react';
import { Typography, Box } from '@mui/material';

import meter from 'packages/novous_web/src/assets/icons/Meter.svg';
import InputField from 'packages/novous_web/src/common/components/InputField';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';

interface NumberConfigItem {
  value?: number;
  thresholdValue?: number;
  min?: number;
  max?: number;
  label?: string;
  error?: string;
  readonly?: boolean;
}

interface AlertConfig {
  displayName: string;
  enabled: boolean;
  expanded: boolean;
  thresholdValue?: number;
  dropdownValue?: string;
  dropdownOptions?: string[];
  min?: number;
  criticalEvent?: boolean;
  max?: number;
  label?: string;
  error?: string;
  numberConfig?: NumberConfigItem[];
  readonly?: boolean;
}

interface AlertSettingsGridProps {
  alertConfigState: Record<string, AlertConfig>;
  alertData: any;
  theme: any;
  onToggle: (key: string) => void;
  onExpand: (key: string) => void;
  onCheckCritical?: (key: string, criticalEvent: boolean) => void;
  onInputChange: (key: string, configIndex: number, newValue: number) => void; // Updated signature
  onDropdownChange?: (key: string, newValue: string) => void;
  onVideoToggle?: (key: string, videoName: string, newValue: boolean) => void;
}

const AlertSettingsGrid: FC<AlertSettingsGridProps> = ({
  alertConfigState,
  alertData,
  theme,
  onToggle,
  onExpand,
  onInputChange,
  onCheckCritical,
  onDropdownChange,
  onVideoToggle,
}) => {
  // Helper function to get configuration for a specific alert type
  const getAlertConfig = (key: string) => {
    return alertData[key] || [];
  };

  // Helper function to check if alert has dropdown configuration
  const hasDropdownConfig = (key: string) => {
    const config = getAlertConfig(key);
    return config.some((item: any) => item.type === 'dropdown');
  };

  // Helper function to get dropdown configuration
  const getDropdownConfig = (key: string) => {
    const config = getAlertConfig(key);
    return config.find((item: any) => item.type === 'dropdown');
  };

  const getVideoConfigs = (key: string) => {
    const config = getAlertConfig(key);
    return config.filter((item: any) => item.name.includes('enableVideo') && item.type === 'boolean');
  };

  return (
    <>
      <div className="w-full grid grid-cols-2 gap-4">
        {alertConfigState &&
          Object.entries(alertConfigState).map(([key, config]) => {
            if (key === 'warningAudioLanguage') return null;

            const dropdownConfig = getDropdownConfig(key);
            const hasDropdown = hasDropdownConfig(key);
            const hasNumber = config.numberConfig && config.numberConfig.length > 0;
            const videoConfigs = getVideoConfigs(key);

            return (
              <div
                key={key}
                className={`flex flex-col gap-2 border border-[#F0EFF8] p-4 rounded-lg ${
                  hasNumber || hasDropdown || videoConfigs.length > 0 ? 'cursor-pointer' : ''
                }`}
                onClick={() => (hasNumber || hasDropdown || videoConfigs.length > 0) && onExpand(key)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <img src={meter} alt="Meter" className="w-6 h-6" />
                    <Typography variant="body2" fontSize="16px" fontWeight={500} lineHeight="26px">
                      {alertData[key]?.[0]?.label || config.displayName}
                    </Typography>
                  </div>

                  <div className="flex items-center gap-3">
                    {typeof config.criticalEvent !== 'undefined' && (
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.criticalEvent || false}
                          onChange={(e) => {
                            e.stopPropagation();
                            onCheckCritical && onCheckCritical(key, !config.criticalEvent);
                          }}
                        />
                        <Typography variant="caption" color="error">
                          Critical
                        </Typography>
                      </label>
                    )}

                    <label
                      className="relative inline-flex items-center cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={config.enabled}
                        onChange={() => onToggle(key)}
                      />
                      <Box
                        className="w-12 h-6 flex items-center rounded-full transition-colors duration-300 ease-in-out"
                        sx={{
                          backgroundColor: config.enabled
                            ? theme.palette.primary.main
                            : theme.palette.secondary.main,
                        }}
                      >
                        <div
                          className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${
                            config.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </Box>
                    </label>
                  </div>
                </div>

                {/* Expanded Configuration Section */}
                {config.expanded &&
                  config.enabled &&
                  (hasNumber || hasDropdown || videoConfigs.length > 0) && (
                    <div className="flex flex-col gap-4 mt-4" onClick={(e) => e.stopPropagation()}>
                      {/* Number Input Fields - Render from numberConfig array */}
                      {hasNumber &&
                        config.numberConfig &&
                        config.numberConfig.map((nc: NumberConfigItem, idx: number) => (
                          <div className="flex flex-col gap-2" key={idx}>
                            <InputField
                              key={nc.label}
                              label={nc.label || `Threshold ${idx + 1}`}
                              value={nc.thresholdValue ?? nc.value ?? ''}
                              onChange={(e) => onInputChange(key, idx, Number(e.target.value) || 0)}
                              type="number"
                              min={nc.min}
                              max={nc.max}
                              readOnly={nc.readonly}
                              isDisabled={nc.readonly}
                            />
                            {nc.error && (
                              <Typography variant="caption" color="error">
                                {nc.error}
                              </Typography>
                            )}
                          </div>
                        ))}

                      {/* Fallback for single value (backward compatibility) */}
                      {!hasNumber && config.thresholdValue !== undefined && (
                        <div className="flex flex-col gap-2">
                          <InputField
                            label={config.label || 'Threshold'}
                            value={config.thresholdValue ?? ''}
                            onChange={(e) => onInputChange(key, 0, Number(e.target.value) || 0)}
                            type="number"
                            min={config.min}
                            max={config.max}
                            readOnly={config.readonly}
                            isDisabled={config.readonly}
                          />
                          {config.error && (
                            <Typography variant="caption" color="error">
                              {config.error}
                            </Typography>
                          )}
                        </div>
                      )}

                      {/* Dropdown Field */}
                      {hasDropdown && dropdownConfig && onDropdownChange && (
                        <div className="flex flex-col gap-2">
                          <CustomDropDownInput
                            label={dropdownConfig.label}
                            options={
                              dropdownConfig.options?.map((option: string) => ({
                                label: option,
                                value: option,
                              })) || []
                            }
                            value={config.dropdownValue || dropdownConfig.value || ''}
                            onChange={(value) => onDropdownChange(key, value)}
                            placeholder={`Select ${dropdownConfig.label}`}
                            showDropdownArrow={true}
                          />
                        </div>
                      )}

                      {/* Video Recording Toggles */}
                      {videoConfigs.length > 0 && onVideoToggle && (
                        <div className="flex flex-col gap-3">
                          {videoConfigs.map((videoConfig: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between">
                              <Typography variant="body2" fontWeight={500}>
                                {videoConfig.label || 'Event Video Recording'}
                              </Typography>
                              <label
                                className="relative inline-flex items-center cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={videoConfig.value}
                                  onChange={() => onVideoToggle(key, videoConfig.name, !videoConfig.value)}
                                />
                                <Box
                                  className="w-12 h-6 flex items-center rounded-full transition-colors duration-300 ease-in-out"
                                  sx={{
                                    backgroundColor: videoConfig.value
                                      ? theme.palette.primary.main
                                      : theme.palette.secondary.main,
                                  }}
                                >
                                  <div
                                    className={`bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ${
                                      videoConfig.value ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </Box>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
              </div>
            );
          })}
      </div>
    </>
  );
};

export default AlertSettingsGrid;
